import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const TARGET_SECONDS = 30;

const DEFAULT_CLONE_SCRIPT = `OH my gosh... I can HARDLY sit still right now.

Because I just discovered something that feels like a **TOTAL game changer**.

For YEARS, I've wanted to share what I know—teaching, explaining ideas, helping people learn faster.

But creating content takes FOREVER.

Recording. Editing. Fixing mistakes. It's exhausting!

But then... I found **{product}**.

With **{product}**, I can create AI videos that look professional—without needing a studio.`;

const DEFAULT_LANGUAGES = [
  "English",
  "Spanish",
  "French",
  "German",
  "Portuguese",
  "Italian",
  "Japanese",
  "Korean",
];

/**
 * @param {{
 *  open: boolean;
 *  onClose: () => void;
 *  lang?: Record<string, string>;
 *  showSnack?: (msg: string, ok?: boolean) => void;
 *  hitAxios?: Function;
 *  onCreated?: (data: { voice_id: string; name: string }) => void;
 * }} props
 */
export default function VoiceCloneModal({
  open,
  onClose,
  lang,
  showSnack,
  hitAxios,
  onCreated,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const panelBg = isDark
    ? alpha(theme.palette.common.black, 0.45)
    : alpha(theme.palette.common.black, 0.04);
  const scriptBg = isDark ? "#1a1a1a" : alpha(theme.palette.common.black, 0.06);

  const [mode, setMode] = React.useState("record");
  const [voiceName, setVoiceName] = React.useState("");
  const [cloneLanguage, setCloneLanguage] = React.useState("English");
  const [devices, setDevices] = React.useState([]);
  const [deviceId, setDeviceId] = React.useState("");
  const [recording, setRecording] = React.useState(false);
  const [elapsed, setElapsed] = React.useState(0);
  const [uploadFile, setUploadFile] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  const mediaRecorderRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const streamRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const uploadInputRef = React.useRef(null);

  const cleanupStreams = React.useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    mediaRecorderRef.current = null;
    setRecording(false);
  }, []);

  React.useEffect(() => {
    if (!open) {
      cleanupStreams();
      setElapsed(0);
      setMode("record");
      setUploadFile(null);
      setVoiceName("");
      setSubmitting(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        /* labels may stay empty until permission */
      }
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const inputs = all.filter((d) => d.kind === "audioinput");
        if (!cancelled) {
          setDevices(inputs);
          setDeviceId((prev) => {
            if (prev && inputs.some((d) => d.deviceId === prev)) return prev;
            return inputs[0]?.deviceId || "";
          });
        }
      } catch {
        if (!cancelled) setDevices([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, cleanupStreams]);

  React.useEffect(() => {
    if (recording && elapsed >= TARGET_SECONDS) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      const mr = mediaRecorderRef.current;
      if (mr && mr.state === "recording") mr.stop();
    }
  }, [elapsed, recording]);

  const submitClone = React.useCallback(
    async (file) => {
      if (!hitAxios) {
        showSnack?.(
          lang?.voicesCloneNoApi || "Voice cloning is unavailable right now.",
          false,
        );
        return;
      }
      if (!file) {
        showSnack?.(
          lang?.voicesCloneNeedAudio || "Record or upload a voice sample first.",
          false,
        );
        return;
      }
      setSubmitting(true);
      try {
        const fd = new FormData();
        fd.append("audio", file, file.name || "voice-sample.webm");
        fd.append(
          "name",
          (voiceName || "").trim() ||
            `My voice ${new Date().toLocaleDateString()}`,
        );
        const res = await hitAxios({
          path: "/api/voice-changer/clone",
          post: true,
          admin: false,
          obj: fd,
        });
        if (res?.data?.success) {
          showSnack?.(
            lang?.voicesCloneSuccess || "Voice created successfully.",
            true,
          );
          onCreated?.(res.data.data);
          onClose?.();
        }
      } finally {
        setSubmitting(false);
      }
    },
    [hitAxios, lang, onClose, onCreated, showSnack, voiceName],
  );

  const stopRecording = React.useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === "recording") mr.stop();
    else cleanupStreams();
  }, [cleanupStreams]);

  const startRecording = React.useCallback(async () => {
    try {
      const constraints = {
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      chunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";
      let mr;
      try {
        mr = mime
          ? new MediaRecorder(stream, { mimeType: mime })
          : new MediaRecorder(stream);
      } catch {
        mr = new MediaRecorder(stream);
      }
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, {
          type: mr.mimeType || "audio/webm",
        });
        const file = new File([blob], "voice-clone-sample.webm", {
          type: blob.type || "audio/webm",
        });
        cleanupStreams();
        setElapsed(0);
        await submitClone(file);
      };
      mr.start(250);
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((s) => s + 1);
      }, 1000);
    } catch {
      showSnack?.(
        lang?.voicesCloneMicError || "Could not access the microphone.",
        false,
      );
    }
  }, [cleanupStreams, deviceId, lang, showSnack, submitClone]);

  const toggleRecord = () => {
    if (submitting) return;
    if (recording) stopRecording();
    else startRecording();
  };

  const formatTime = (s) => {
    const sec = Math.min(s, TARGET_SECONDS);
    return `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
  };

  const productName = lang?.voicesCloneScriptProduct || "the platform";

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: "background.paper",
          backgroundImage: "none",
          border: 1,
          borderColor: "divider",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          px: 2,
          pt: 2,
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, pr: 2 }}>
          {lang?.voicesCloneModalTitle || "Create voice clone"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          disabled={submitting}
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 0, px: 2, pb: 2 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          {lang?.voicesCloneInstructions ||
            "Speak with high energy, and record for at least 30 sec with a professional mic or smartphone."}
        </Typography>

        <TextField
          fullWidth
          size="small"
          label={lang?.voicesCloneNameLabel || "Voice name"}
          placeholder={lang?.voicesCloneNamePlaceholder || "e.g. My narration voice"}
          value={voiceName}
          onChange={(e) => setVoiceName(e.target.value)}
          disabled={submitting || recording}
          sx={{ mb: 2 }}
        />

        <ToggleButtonGroup
          exclusive
          fullWidth
          value={mode}
          onChange={(_, v) => v && !submitting && setMode(v)}
          sx={{
            mb: 2,
            bgcolor: panelBg,
            p: 0.5,
            borderRadius: 2,
            "& .MuiToggleButton-root": {
              flex: 1,
              textTransform: "none",
              fontWeight: 600,
              border: "none",
              borderRadius: "8px !important",
              py: 1.25,
              gap: 1,
            },
            "& .Mui-selected": {
              bgcolor: isDark ? "grey.800" : "background.paper",
              boxShadow: 1,
            },
          }}
        >
          <ToggleButton value="record" disabled={submitting}>
            <MicIcon fontSize="small" />
            {lang?.voicesCloneTabRecord || "Record audio"}
          </ToggleButton>
          <ToggleButton value="upload" disabled={submitting}>
            <CloudUploadIcon fontSize="small" />
            {lang?.voicesCloneTabUpload || "Upload audio"}
          </ToggleButton>
        </ToggleButtonGroup>

        {mode === "record" && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
            <Select
              size="small"
              fullWidth
              value={cloneLanguage}
              onChange={(e) => setCloneLanguage(e.target.value)}
              displayEmpty
              disabled={submitting || recording}
            >
              {DEFAULT_LANGUAGES.map((lng) => (
                <MenuItem key={lng} value={lng}>
                  {lng}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              fullWidth
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              displayEmpty
              disabled={submitting || recording}
              renderValue={(id) => {
                if (!devices.length) {
                  return lang?.voicesCloneLoadingDevices || "Loading devices…";
                }
                const d = devices.find((x) => x.deviceId === id);
                return d?.label || lang?.voicesCloneDefaultMic || "Default microphone";
              }}
            >
              {devices.map((d) => (
                <MenuItem key={d.deviceId} value={d.deviceId}>
                  {d.label || `Microphone ${d.deviceId.slice(0, 6)}…`}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}

        {mode === "upload" && (
          <Box sx={{ mb: 2 }}>
            <input
              ref={uploadInputRef}
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.webm,.ogg"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setUploadFile(f);
                e.target.value = "";
              }}
            />
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              {lang?.voicesCloneUploadHint ||
                "Choose a clean voice sample (WAV, MP3, M4A, etc.)."}
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              disabled={submitting}
              onClick={() => uploadInputRef.current?.click()}
              sx={{ textTransform: "none", py: 1.5 }}
            >
              {uploadFile
                ? uploadFile.name
                : lang?.voicesCloneUploadBrowse || "Browse files"}
            </Button>
            {uploadFile ? (
              <Button
                fullWidth
                variant="contained"
                disabled={submitting}
                onClick={() => submitClone(uploadFile)}
                sx={{ mt: 1.5, textTransform: "none", fontWeight: 700 }}
              >
                {submitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  lang?.voicesCloneSubmit || "Create voice"
                )}
              </Button>
            ) : null}
          </Box>
        )}

        <Box
          sx={{
            bgcolor: scriptBg,
            borderRadius: 2,
            p: 2,
            mb: 2,
            maxHeight: 280,
            overflow: "auto",
            border: 1,
            borderColor: "divider",
          }}
        >
          <Typography
            variant="body2"
            component="div"
            sx={{ whiteSpace: "pre-wrap", lineHeight: 1.75 }}
          >
            {(lang?.voicesCloneScript || DEFAULT_CLONE_SCRIPT)
              .replace(/\{product\}/g, productName)
              .split("\n\n")
              .filter(Boolean)
              .map((para, i, arr) => (
                <Box key={i} sx={{ mb: i < arr.length - 1 ? 1.5 : 0 }}>
                  {para.split("**").map((chunk, j) =>
                    j % 2 === 1 ? (
                      <Typography component="span" key={j} fontWeight={700}>
                        {chunk}
                      </Typography>
                    ) : (
                      chunk
                    ),
                  )}
                </Box>
              ))}
          </Typography>
        </Box>

        {mode === "record" && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 1 }}
          >
            <Typography
              variant="body2"
              sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}
            >
              {formatTime(Math.min(elapsed, TARGET_SECONDS))}
            </Typography>
            <IconButton
              onClick={toggleRecord}
              disabled={submitting}
              sx={{
                width: 64,
                height: 64,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                "&:hover": { bgcolor: "primary.dark" },
                boxShadow: 3,
              }}
              aria-label={recording ? "Stop recording" : "Start recording"}
            >
              {submitting ? (
                <CircularProgress size={26} color="inherit" />
              ) : recording ? (
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    bgcolor: "currentColor",
                    borderRadius: 0.5,
                  }}
                />
              ) : (
                <MicIcon sx={{ fontSize: 32 }} />
              )}
            </IconButton>
            <Typography
              variant="body2"
              sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}
            >
              {formatTime(TARGET_SECONDS)}
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
